import React, { useState, useEffect } from 'react';
import { usePreferredBible } from '../../hooks/usePreferredBible';
import { formatBookName } from '../../utils/bibleBooks';
import { useNavigate } from 'react-router-dom';
import { useBibleStore } from '../../../../shared/store/bibleStore';
import { BibleNavigation } from './BibleNavigation';
import { bibleService } from '../../../../shared/services/bibleService';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Header } from '../../../../shared/components/layout/Header';
import { Spinner } from '../../../../shared/components/common/Spinner';

interface BibleReaderProps {
  initialBook?: string;
  initialChapter?: string;
}

export function BibleReader({ initialBook = '1', initialChapter = '1' }: BibleReaderProps) {
  const navigate = useNavigate();
  const { preferredBible, loading: loadingBible } = usePreferredBible();
  const { lastReadPosition, saveReadingPosition, verseContents, isLoading, loadChapterVerses, setSelectedVerse } = useBibleStore();
  const [currentBookName, setCurrentBookName] = useState('Genesis');
  const [maxChapter, setMaxChapter] = useState(0);
  
  // Initial URL sync effect
  useEffect(() => {
    const syncWithLastPosition = async () => {
      if (lastReadPosition && (initialBook === '1' && initialChapter === '1')) {
        const { name } = await bibleService.getBookInfo(lastReadPosition.book);
        navigate(`/bible/${name}/${lastReadPosition.chapter}`, { replace: true });
      }
    };
    syncWithLastPosition();
  }, [lastReadPosition]);
  
  const [selectedBook, setSelectedBook] = useState<string>(
    lastReadPosition?.book || initialBook
  );
  const [selectedChapter, setSelectedChapter] = useState<string>(
    lastReadPosition?.chapter || initialChapter
  );
  
  // Get book name and chapter count when selected book changes
  useEffect(() => {
    const loadBookInfo = async () => {
      const { name } = await bibleService.getBookInfo(selectedBook);
      setCurrentBookName(name);
      const count = await bibleService.getChapterCount(selectedBook);
      setMaxChapter(count);
    };
    loadBookInfo();
  }, [selectedBook]);

  // Initial load
  useEffect(() => {
    if (loadingBible) return;

    const loadInitialChapter = async () => {
      // Only load verses if we don't have them
      const hasVerses = Object.keys(verseContents).some(k => k.startsWith(`${selectedBook}-${selectedChapter}`));
      if (!hasVerses && !isLoading) {
        await loadChapterVerses(selectedBook, selectedChapter, preferredBible);
      }
      
      // Always save the reading position
      await saveReadingPosition(selectedBook, selectedChapter);
    };

    loadInitialChapter();
  }, [selectedBook, selectedChapter, loadingBible, preferredBible]);

  const handleNavigate = async (book: string, chapter: string) => {
    const { name } = await bibleService.getBookInfo(book);
    navigate(`/bible/${name}/${chapter}`, { replace: true });
    setSelectedBook(book);
    setSelectedChapter(chapter);
    await saveReadingPosition(book, chapter);
  };

  const handlePreviousChapter = async () => {
    if (Number(selectedChapter) > 1) {
      handleNavigate(selectedBook, String(Number(selectedChapter) - 1));
    }
  };

  const handleNextChapter = async () => {
    if (Number(selectedChapter) < maxChapter) {
      handleNavigate(selectedBook, String(Number(selectedChapter) + 1));
    }
  };

  // Show loading state while Bible is loading or verses are loading
  if (loadingBible || isLoading) {
    return (
      <div>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Spinner variant="border" size="lg" className="text-[#361E06] mb-4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="w-full mt-[60px] border-b border-t border-[rgba(0,0,0,0.1)]">
        <BibleNavigation
          selectedBook={selectedBook}
          selectedChapter={selectedChapter}
          onNavigate={handleNavigate}
        />
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-16">
          <h1 className="text-3xl font-medium text-[#361E06]">{currentBookName}</h1>
          <h2 className="text-7xl font-newsreader font-bold text-[#361E06] mt-2">Chapter {selectedChapter}</h2>
        </div>

        <div className="space-y-4">
          {Object.entries(verseContents)
            .filter(([key]) => key.startsWith(`${selectedBook}-${selectedChapter}`))
            .sort(([keyA], [keyB]) => {
              const verseA = parseInt(keyA.split('-')[2]);
              const verseB = parseInt(keyB.split('-')[2]);
              return verseA - verseB;
            })
            .map(([key, text]) => {
              const verseNumber = key.split('-')[2];
              return (
                <div 
                  key={key}
                  className="relative group h-full flex flex-col"
                  onClick={() => setSelectedVerse({
                    book: currentBookName,
                    chapter: selectedChapter,
                    verse: verseNumber
                  })}
                >
                  <div className="flex flex-col justify-between h-full">
                    <div className="flex flex-col justify-between">
                      <div className="text-[#441A0B] whitespace-pre-wrap break-words font-serif text-[22px] hover:underline cursor-pointer">
                        <span className="font-sans text-xs font-semibold text-[rgba(54,30,6,0.5)] mr-2">
                          {verseNumber}
                        </span>
                        {text}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handlePreviousChapter}
            disabled={Number(selectedChapter) <= 1}
            className="flex items-center gap-2 px-4 py-2 text-[#361E06] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgba(0,0,0,0.05)] rounded-lg transition-colors"
          >
            <CaretLeft className="w-5 h-5" weight="bold" />
            Previous Chapter
          </button>
          <button
            onClick={handleNextChapter}
            disabled={Number(selectedChapter) >= maxChapter}
            className="flex items-center gap-2 px-4 py-2 text-[#361E06] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgba(0,0,0,0.05)] rounded-lg transition-colors"
          >
            Next Chapter
            <CaretRight className="w-5 h-5" weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
